import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ClientDto, ClientTransferDto, clientsApi, handleApiError } from '../services/clientsService';
import { usersApi, UserDto } from '../services/usersService';

interface ClientTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: ClientDto | null;
  onTransferComplete: () => void;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: 500px;
  direction: rtl;
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ClientInfo = styled.div`
  background: var(--surface-light, #f8f9fa);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 16px;
  margin-bottom: 24px;
`;

const ClientName = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const ClientDetails = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const FormField = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const RequiredMark = styled.span`
  color: var(--error-color);
  margin-right: 4px;
`;

const Select = styled.select<{ hasError?: boolean; isValid?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props =>
    props.hasError ? 'var(--error-color)' :
    props.isValid ? 'rgba(0, 202, 114, 0.5)' :
    'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: ${props => props.isValid ? 'rgba(0, 202, 114, 0.03)' : 'white'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props =>
      props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props =>
      props.hasError ? 'rgba(251, 39, 93, 0.1)' : 'var(--primary-light)'};
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean; isValid?: boolean }>`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid ${props =>
    props.hasError ? 'var(--error-color)' :
    props.isValid ? 'rgba(0, 202, 114, 0.5)' :
    'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  background: ${props => props.isValid ? 'rgba(0, 202, 114, 0.03)' : 'white'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props =>
      props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props =>
      props.hasError ? 'rgba(251, 39, 93, 0.1)' : 'var(--primary-light)'};
  }
`;


const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  font-size: 14px;
  border: 1px solid var(--error-color);
`;

const ValidationError = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:before {
    content: '⚠';
    font-size: 14px;
  }
`;

const ValidationSuccess = styled.div`
  color: #059669;
  font-size: 12px;
  margin-top: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;

  &:before {
    content: '✓';
    font-size: 13px;
  }
`;

const ModalFooter = styled.div`
  padding: 0 24px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary'
    ? `
      background: var(--primary-color);
      color: white;
      border: 1px solid var(--primary-color);
      &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
      &:disabled {
        background: #e0e0e0;
        border-color: #e0e0e0;
        color: #9e9e9e;
        cursor: not-allowed;
        opacity: 1;
      }
    `
    : `
      background: var(--surface-color);
      color: var(--text-primary);
      border: 1px solid var(--border-color);
      &:hover:not(:disabled) { background: var(--hover-background); }
      &:disabled {
        background: #f5f5f5;
        color: #bdbdbd;
        cursor: not-allowed;
      }
    `
  }
`;

const ClientTransferModal: React.FC<ClientTransferModalProps> = ({
  isOpen,
  onClose,
  client,
  onTransferComplete
}) => {
  const [caseManagers, setCaseManagers] = useState<UserDto[]>([]);
  const [caseManagerLoads, setCaseManagerLoads] = useState<Record<number, number>>({});
  const [selectedCaseManager, setSelectedCaseManager] = useState<string>('');
  const [transferReason, setTransferReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    caseManager?: string;
    reason?: string;
  }>({});
  const [validationSuccess, setValidationSuccess] = useState<{
    caseManager?: boolean;
    reason?: boolean;
  }>({});
  const [touched, setTouched] = useState<{
    caseManager?: boolean;
    reason?: boolean;
  }>({});

  useEffect(() => {
    const loadData = async () => {
      if (!isOpen) return;
      
      try {
        const [caseManagersData, loadsData] = await Promise.all([
          usersApi.getCaseManagers(),
          clientsApi.getCaseManagerLoads()
        ]);
        
        setCaseManagers(caseManagersData);
        setCaseManagerLoads(loadsData);
      } catch (err) {
        setError(handleApiError(err));
      }
    };

    loadData();
  }, [isOpen]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedCaseManager('');
      setTransferReason('');
      setError(null);
      setValidationErrors({});
      setValidationSuccess({});
      setTouched({});
    }
  }, [isOpen, client]);

  const validateCaseManager = (value: string): string | null => {
    if (!value) {
      return 'יש לבחור מנהל תיק להעברת הלקוח';
    }
    if (parseInt(value) === client?.assignedCaseManagerId) {
      return 'לא ניתן להעביר לקוח לאותו מנהל תיק הנוכחי';
    }
    return null;
  };

  const validateTransferReason = (value: string): string | null => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return 'יש להזין סיבה מפורטת להעברת הלקוח';
    }
    if (trimmedValue.length < 10) {
      return `סיבת ההעברה חייבת להכיל לפחות 10 תווים (נותרו ${10 - trimmedValue.length} תווים)`;
    }
    return null;
  };

  const validateForm = (): boolean => {
    const errors: { caseManager?: string; reason?: string } = {};
    const success: { caseManager?: boolean; reason?: boolean } = {};

    const caseManagerError = validateCaseManager(selectedCaseManager);
    const reasonError = validateTransferReason(transferReason);

    if (caseManagerError) {
      errors.caseManager = caseManagerError;
    } else {
      success.caseManager = true;
    }

    if (reasonError) {
      errors.reason = reasonError;
    } else {
      success.reason = true;
    }

    setValidationErrors(errors);
    setValidationSuccess(success);
    return Object.keys(errors).length === 0;
  };

  const handleCaseManagerChange = (value: string) => {
    setSelectedCaseManager(value);
    setTouched(prev => ({ ...prev, caseManager: true }));
    
    const error = validateCaseManager(value);
    setValidationErrors(prev => ({ ...prev, caseManager: error || undefined }));
    setValidationSuccess(prev => ({ ...prev, caseManager: !error }));
  };

  const handleReasonChange = (value: string) => {
    setTransferReason(value);
    setTouched(prev => ({ ...prev, reason: true }));
    
    const error = validateTransferReason(value);
    setValidationErrors(prev => ({ ...prev, reason: error || undefined }));
    setValidationSuccess(prev => ({ ...prev, reason: !error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!client || !validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const transferData: ClientTransferDto = {
        clientId: client.id,
        newCaseManagerId: parseInt(selectedCaseManager),
        transferReason: transferReason.trim()
      };

      await clientsApi.transferClient(transferData);
      onTransferComplete();
      onClose();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Track where mousedown started to prevent closing when selecting text
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  if (!client) return null;

  // Filter out the currently assigned case manager
  const availableCaseManagers = caseManagers.filter(
    manager => manager.id !== client.assignedCaseManagerId
  );

  return (
    <ModalOverlay $isOpen={isOpen} onMouseDown={handleMouseDown} onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>העברת לקוח בין מנהלי תיקים</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <ClientInfo>
              <ClientName>{client.fullName}</ClientName>
              <ClientDetails>
                תעודת זהות: {client.idNumber} | טלפון: {client.phone}<br />
                מנהל תיק נוכחי: {client.assignedCaseManagerName || 'לא מוקצה'}
              </ClientDetails>
            </ClientInfo>

            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}

            <FormField>
              <Label>
                <RequiredMark>*</RequiredMark>
                מנהל תיק חדש
              </Label>
              <Select
                value={selectedCaseManager}
                onChange={(e) => handleCaseManagerChange(e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, caseManager: true }))}
                hasError={touched.caseManager && !!validationErrors.caseManager}
                isValid={touched.caseManager && validationSuccess.caseManager}
                required
              >
                <option value="">בחר מנהל תיק</option>
                {availableCaseManagers.map(manager => (
                  <option key={manager.id} value={manager.id}>
                    {manager.fullName} ({caseManagerLoads[manager.id] || 0} תיקים פעילים)
                  </option>
                ))}
              </Select>
              {touched.caseManager && validationErrors.caseManager && (
                <ValidationError>{validationErrors.caseManager}</ValidationError>
              )}
              {touched.caseManager && validationSuccess.caseManager && !validationErrors.caseManager && (
                <ValidationSuccess>מנהל תיק נבחר בהצלחה</ValidationSuccess>
              )}
            </FormField>

            <FormField>
              <Label>
                <RequiredMark>*</RequiredMark>
                סיבת ההעברה
              </Label>
              <TextArea
                value={transferReason}
                onChange={(e) => handleReasonChange(e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, reason: true }))}
                hasError={touched.reason && !!validationErrors.reason}
                isValid={touched.reason && validationSuccess.reason}
                placeholder="אנא פרט את הסיבה להעברת הלקוח (לפחות 10 תווים) - לדוגמה: רמת עומס גבוהה, התמחות מתאימה יותר, בקשת הלקוח"
                required
                minLength={10}
              />
              {touched.reason && validationErrors.reason && (
                <ValidationError>{validationErrors.reason}</ValidationError>
              )}
              {touched.reason && validationSuccess.reason && !validationErrors.reason && (
                <ValidationSuccess>סיבת ההעברה תקינה ומפורטת</ValidationSuccess>
              )}
            </FormField>
          </ModalBody>

          <ModalFooter>
            <Button type="button" onClick={onClose}>
              ביטול
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              disabled={loading || !selectedCaseManager || !transferReason.trim()}
            >
              {loading ? 'מעביר...' : 'העבר לקוח'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ClientTransferModal;